import { useState } from 'react'
import { Button, DatePicker, Form, Input, InputNumber, Modal, Select, Tag, App } from 'antd'
import { PlusOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { leaveApplicationsApi, leaveTypesApi, employeesApi } from '@/features/hr/api'
import type { LeaveApplication } from '@/shared/types/hr'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import dayjs from 'dayjs'

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'orange',
  APPROVED: 'green',
  REJECTED: 'red',
}

export default function LeaveApplicationsPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [approveForm] = Form.useForm()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [approveModal, setApproveModal] = useState<{ open: boolean; id: string; action: string }>({ open: false, id: '', action: '' })

  const canCreate = usePermission('leave.create')
  const canApprove = usePermission('leave.approve')

  const { data, isLoading } = useQuery({
    queryKey: ['leave-applications', page, pageSize, statusFilter],
    queryFn: () => leaveApplicationsApi.list({ page, size: pageSize, status: statusFilter }),
  })

  const { data: employeesData } = useQuery({
    queryKey: ['employees-all'],
    queryFn: () => employeesApi.list({ page: 1, size: 100, is_active: true }),
  })

  const { data: leaveTypesData } = useQuery({
    queryKey: ['leave-types'],
    queryFn: () => leaveTypesApi.list(),
  })

  const empOptions = employeesData?.items.map((e) => ({ label: `${e.emp_code} — ${e.emp_name}`, value: e.id })) ?? []
  const ltOptions = leaveTypesData?.map((lt) => ({ label: lt.leave_name, value: lt.id })) ?? []

  const openCreate = () => {
    form.resetFields()
    setDrawerOpen(true)
  }

  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const range = values.date_range as [dayjs.Dayjs, dayjs.Dayjs]
      return leaveApplicationsApi.create({
        employee_id: values.employee_id,
        leave_type_id: values.leave_type_id,
        from_date: range[0].format('YYYY-MM-DD'),
        to_date: range[1].format('YYYY-MM-DD'),
        days: values.days,
        reason: values.reason,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leave-applications'] })
      message.success('Leave application submitted')
      setDrawerOpen(false)
    },
    onError: () => message.error('Submit failed'),
  })

  const approveMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: string; status: string; remarks?: string }) =>
      leaveApplicationsApi.approve(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leave-applications'] })
      qc.invalidateQueries({ queryKey: ['leave-balances'] })
      message.success('Leave application updated')
      setApproveModal({ open: false, id: '', action: '' })
    },
    onError: () => message.error('Action failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => createMutation.mutate(v))

  const onApprove = () => {
    approveForm.validateFields().then((v) => {
      approveMutation.mutate({ id: approveModal.id, status: approveModal.action, remarks: v.remarks })
    })
  }

  const columns = [
    { title: 'Employee', dataIndex: 'employee_name', key: 'employee_name' },
    { title: 'Leave Type', dataIndex: 'leave_type_name', key: 'leave_type_name' },
    { title: 'From', dataIndex: 'from_date', key: 'from_date', width: 110 },
    { title: 'To', dataIndex: 'to_date', key: 'to_date', width: 110 },
    { title: 'Days', dataIndex: 'days', key: 'days', width: 70 },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 180,
      render: (_: unknown, record: LeaveApplication) => {
        if (record.status !== 'PENDING' || !canApprove) return record.remarks ?? '—'
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => { approveForm.resetFields(); setApproveModal({ open: true, id: record.id, action: 'APPROVED' }) }}>Approve</Button>
            <Button size="small" danger icon={<CloseOutlined />} onClick={() => { approveForm.resetFields(); setApproveModal({ open: true, id: record.id, action: 'REJECTED' }) }}>Reject</Button>
          </div>
        )
      },
    },
  ]

  return (
    <div>
      <PageHeader
        title="Leave Applications"
        subtitle="Apply for and manage leave requests"
        extra={canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Apply Leave</Button>}
      />

      <div style={{ marginBottom: 16 }}>
        <Select allowClear placeholder="Filter by status" options={['PENDING', 'APPROVED', 'REJECTED'].map(s => ({ label: s, value: s }))} onChange={(v) => { setStatusFilter(v); setPage(1) }} style={{ width: 160 }} />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data?.items} loading={isLoading} total={data?.total} page={page} pageSize={pageSize} onPageChange={(p, ps) => { setPage(p); setPageSize(ps) }} />

      <FormDrawer title="Apply Leave" open={drawerOpen} onClose={() => setDrawerOpen(false)} onSubmit={onSubmit} loading={createMutation.isPending} form={form}>
        <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" options={empOptions} placeholder="Select employee" /></Form.Item>
        <Form.Item name="leave_type_id" label="Leave Type" rules={[{ required: true }]}><Select options={ltOptions} placeholder="Select leave type" /></Form.Item>
        <Form.Item name="date_range" label="From — To" rules={[{ required: true }]}><DatePicker.RangePicker style={{ width: '100%' }} /></Form.Item>
        <Form.Item name="days" label="Days" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0.5} step={0.5} precision={1} /></Form.Item>
        <Form.Item name="reason" label="Reason"><Input.TextArea rows={3} /></Form.Item>
      </FormDrawer>

      <Modal title={approveModal.action === 'APPROVED' ? 'Approve Leave' : 'Reject Leave'} open={approveModal.open} onOk={onApprove} onCancel={() => setApproveModal({ open: false, id: '', action: '' })} confirmLoading={approveMutation.isPending}>
        <Form form={approveForm} layout="vertical">
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
